import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';

import { API } from '../../config';

const DEFAULT_BANNERS = [
  {
    id: 'hero-train-banner',
    title: 'Experience Seamless Train Travel',
    subtitle: 'Track real-time train status, check instant PNR updates & enjoy fresh pantry meals delivered right to your seat.',
    badge: 'LIVE TRACKING & FOOD',
    image_url: '/train-bg.jpg',
    layout: 'full',
    gradient: 'sunset',
    cta_text: 'Check Live Status',
    cta_link: '/live-train-status',
  },
  {
    id: 'food-train-banner',
    title: 'Hot Meals Delivered At Your Berth',
    subtitle: 'Order from top FSSAI approved restaurants across 500+ railway stations.',
    badge: 'FOOD ON TRAIN',
    gradient: 'lime',
    layout: 'half',
    cta_text: 'Order Food',
    cta_link: '/food-on-train',
  },
  {
    id: 'pnr-banner',
    title: 'Instant PNR Confirmation & Platform Info',
    subtitle: '1-tap check with prediction accuracy and coach position alerts.',
    badge: 'PNR STATUS',
    gradient: 'violet',
    layout: 'card',
    cta_text: 'Check PNR',
    cta_link: '/pnr-status',
  }
];

/**
 * Vibrant promo banner carousel (Ixigo-inspired) with auto-rotate, dots,
 * swipe and gradient backgrounds (admin-managed via /admin/banners).
 */
const BannerCarousel = () => {
  const [banners, setBanners] = useState(DEFAULT_BANNERS);
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);
  const startX = useRef(0);

  useEffect(() => {
    axios.get(`${API}/banners`)
      .then(r => {
        const list = r.data?.banners || [];
        setBanners(list.length ? list : DEFAULT_BANNERS);
      })
      .catch(() => setBanners(DEFAULT_BANNERS));
  }, []);

  // auto-rotate
  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);

  if (!banners.length) return null;

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 50) setIdx(i => (i - 1 + banners.length) % banners.length);
    else if (dx < -50) setIdx(i => (i + 1) % banners.length);
  };

  return (
    <div className="relative w-full" data-testid="banner-carousel">
      <div
        className="overflow-hidden rounded-3xl"
        ref={trackRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {banners.map((b, i) => <BannerSlide key={b.id || i} b={b} />)}
        </div>
      </div>

      {/* Dots */}
      {banners.length > 1 && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-3 flex items-center gap-1.5 z-10">
          {banners.map((_, i) => (
            <button
              key={i}
              aria-label={`Slide ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-7 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const GRAD_CLASSES = ['violet', 'coral', 'lime', 'pink', 'sky', 'sunset', 'midnight'];

const BANNER_IMAGE_FALLBACKS = {
  'Flat 40% Off': '/food-card-bg.jpg',
  '40%': '/food-card-bg.jpg',
  'Food on Train': '/food-card-bg.jpg',
  'Travel Stories': '/stories-card-bg.jpg',
  'Track Any Train': '/live-card-bg.jpg',
  'Live Status': '/live-card-bg.jpg',
  'PNR Status': '/pnr-card-bg.jpg',
  'Experience Seamless Train Travel': '/train-bg.jpg',
  'violet': '/food-card-bg.jpg',
  'coral': '/live-card-bg.jpg',
  'lime': '/pnr-card-bg.jpg',
  'sky': '/stories-card-bg.jpg',
  'sunset': '/train-bg.jpg',
  'pink': '/stories-card-bg.jpg',
};

const BannerSlide = ({ b }) => {
  const gradient = (b.gradient || 'violet').toLowerCase();
  const klass = `bnr-${GRAD_CLASSES.includes(gradient) ? gradient : 'violet'}`;
  
  const getBannerImage = () => {
    if (b.image_url) return b.image_url;
    const text = ((b.title || '') + ' ' + (b.subtitle || '') + ' ' + (b.code || '')).toLowerCase();
    if (text.includes('40') || text.includes('food') || text.includes('meal') || text.includes('order')) {
      return '/food-card-bg.jpg';
    }
    if (text.includes('track') || text.includes('live')) {
      return '/live-card-bg.jpg';
    }
    if (text.includes('pnr') || text.includes('berth') || text.includes('coach')) {
      return '/pnr-card-bg.jpg';
    }
    if (text.includes('story') || text.includes('stories') || text.includes('travel') || text.includes('konkan')) {
      return '/stories-card-bg.jpg';
    }
    return BANNER_IMAGE_FALLBACKS[b.title] || BANNER_IMAGE_FALLBACKS[gradient] || '/food-card-bg.jpg';
  };

  const imgUrl = getBannerImage();

  return (
    <div className={`shrink-0 w-full relative overflow-hidden ${klass} rounded-3xl shadow-xl`} style={{ minHeight: 280 }} data-testid="banner-slide-full">
      <img
        src={imgUrl}
        alt={b.title || 'Banner'}
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(8,6,20,0.92) 0%, rgba(8,6,20,0.65) 45%, rgba(8,6,20,0.2) 80%)' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(8,6,20,0.7) 0%, transparent 60%)' }} />
      <BannerContent b={{ ...b, image_url: imgUrl }} />
    </div>
  );
};

// Overlay content used by full/half image layouts (text always light on the scrim).
const BannerContent = ({ b, narrow }) => (
  <div className="relative z-10 h-full flex items-center px-5 md:px-10 py-7" style={{ minHeight: 260 }}>
    <div className={narrow ? 'w-[64%] md:w-1/2' : 'w-full md:w-3/5'} style={{ textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}>
      <BannerText b={b} darkText={false} />
    </div>
  </div>
);

const BannerText = ({ b, darkText }) => (
  <>
    {b.badge && (
      <span className="pill-tag mb-3" style={darkText ? { color: '#13111C' } : { color: '#5B21B6' }}>
        <Sparkles size={11} /> {b.badge}
      </span>
    )}
    <h2 className={`font-serif-display text-3xl md:text-5xl leading-[1.05] ${darkText ? 'text-tp-ink' : 'text-white'}`}>
      {b.title || 'Special Offer'}
    </h2>
    <p className={`mt-2 text-sm md:text-base ${darkText ? 'text-tp-ink-soft' : 'text-white/90'} max-w-md`}>
      {b.subtitle || ''}
    </p>
    {b.code && (
      <div className="mt-3 inline-flex items-center gap-2 bg-black/30 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold tracking-wider text-white">
        Code: <span className="font-mono">{b.code}</span>
      </div>
    )}
    {b.cta_text && (
      <div className="mt-4">
        <Link
          to={b.cta_link || '/'}
          className="inline-flex items-center gap-2 bg-white text-tp-ink px-5 py-2.5 rounded-full font-bold text-sm hover:scale-[1.03] active:scale-[0.98] transition-transform shadow-tp-soft"
        >
          {b.cta_text} <ArrowRight size={14} />
        </Link>
      </div>
    )}
  </>
);

const BannerDecor = ({ gradient }) => {
  const emojiMap = { violet: '🚄', coral: '🛤️', lime: '✨', pink: '🎟️', sky: '🌤️', sunset: '🌅', midnight: '🌙' };
  return (
    <div className="text-[140px] leading-none animate-float-slow opacity-90" style={{ filter: 'drop-shadow(0 12px 12px rgba(0,0,0,0.25))' }}>
      {emojiMap[gradient] || '🚆'}
    </div>
  );
};

export default BannerCarousel;
